import React, { useState, useMemo, useCallback } from "react";
import {
  AllCommunityModule,
  ModuleRegistry,
  ValidationModule,
} from "ag-grid-community";
// Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css";
import "@/App.css";
import { AgGridReact } from "ag-grid-react";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import { formatFechaHoraEvento } from "@/lib/utilsUI";

ModuleRegistry.registerModules([AllCommunityModule]);
if (import.meta.env.NODE_ENV !== "production") {
  // only include validation module in dev mode to reduce bundle size
  ModuleRegistry.registerModules([ValidationModule]);
}
// Components
import OpcionesInvitacion from "./OpcionesInvitacion";
import AplicarSeleccion from "./OpcionesInvitacion/AplicarSeleccion";

export default function DataTableClients({
  statsHistorialDeEntradas = { data: [], pagination: {} },
}) {
  // In your DataTableClients component, add state management:
  const [selections, setSelections] = useState({});
  const [appliedStatuses, setAppliedStatuses] = useState({});
  const [isLoading, setIsLoading] = useState(false); // For loading state during apply action

  // Handle selection changes
  const handleSelectionChange = useCallback((rowId, option) => {
    setSelections((prev) => ({
      ...prev,
      [rowId]: option,
    }));
  }, []);

  // Apply selections
  const applySelections = () => {
    setAppliedStatuses((prev) => ({
      ...prev,
      ...selections,
    }));
    setSelections({}); // Clear pending selections
  };

  // Transform backend data to match the expected format
  const rowData = useMemo(() => {
    if (
      !statsHistorialDeEntradas?.data ||
      statsHistorialDeEntradas.data.length === 0
    ) {
      return [];
    }

    return statsHistorialDeEntradas.data.map((item, index) => ({
      id: index + 1,
      rut: item.numeroTributario || "N/A",
      nombre: item.userBuyerName || "N/A",
      edad: item.edad || "N/A",
      genero: item.genero || "N/A",
      instagram: item.instagram || "N/A",
      tipoTicket: item.ticketTypeName || "N/A",
      embajador: item.embajador || "No aplica",
      horarioAccedio: item.fechaHoraValidacion
        ? formatFechaHoraEvento(
            parseUTCDate(
              item.fechaHoraValidacion,
              DEFAULT_TIMEZONE,
            )?.toJSDate(),
            DEFAULT_TIMEZONE,
          )
        : "N/A",
    }));
  }, [statsHistorialDeEntradas]);

  // Sample data que debe venir de una API o base de datos
  // const example = [{
  //     id: 1, // ID único del cliente
  //     rut: 12345678, // Sin puntos ni guion
  //     nombre: "Christopher Ferrada", // Nombre completo del cliente
  //     instagram: "crisferrada", // Sin @ y linkeado a su perfil de Instagram al dar click se abre un modal mostrando su perfil
  //     edad: 28, // edad calculada a partir de su fecha de nacimiento
  //     genero: "Masculino", // Masculino, Femenino, Otro
  //     tipoTicket: "Invitación Free", // Invitación Free, VIP, General u otros conforme a los tipos de ticket que maneje el evento
  //     tipoDistribucion: "Invitación", // Venta Directa, Invitación
  //     tipoInvitacion: "Cortesía", // Cortesía, Con Descuento. No aplica si es Venta Directa
  //     estadoCliente: "normal", // normal, nuevo, destacado, advertencia, bloqueado
  //     embajador: "Manuel Vargas", // Nombre del embajador que invitó a esta persona o "No aplica" cuando es venta directa
  //     liderStaff: "Sofía Martínez",
  //     estadoInvitacion: "pendiente" , // Si es 'noaplica' simplement colocar que no aplica, pero si dice 'pendiente' Renderizar Componente <OpcionesInvitacion /> Si no se ha elegido una opción mostrar las siguientes opciones: Si, Descuento, No. Si se ha elegido una opción mostrar el icono 'fa-circle-check' verde si se aprobó la invitación, el icono 'fa-circle-minus' en amarillo si se dió con descuento y el icono 'fa-xmark' en rojo si se rechazó la invitación
  //     horarioAccedio: "18:30",
  //     asistencias: 3, // Cuantas veces ha asistido esta persona a los eventos que ha creado la empresa
  //   }];

  // estadoCliente badge select renderer
  const EstadoClienteCellRenderer = (params) => {
    const estado = params.value;
    let bgColor = "bg-gray-200 text-gray-800 block w-full";
    let text = "Desconocido";
    switch (estado) {
      case "normal":
        bgColor = "bg-green-400 text-aw-bluedark block w-full";
        text = "Normal";
        break;
      case "nuevo":
        bgColor = "bg-white text-aw-bluedark block w-full";
        text = "Nuevo";
        break;
      case "destacado":
        bgColor = "bg-blue-400 text-aw-bluedark block w-full";
        text = "Destacado";
        break;
      case "advertencia":
        bgColor = "bg-yellow-500 text-aw-bluedark block w-full";
        text = "Advertencia";
        break;
      case "bloqueado":
        bgColor = "bg-red-400 text-aw-bluedark block w-full";
        text = "Bloqueado";
        break;
      default:
        // keep defaults
        break;
    }
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${bgColor}`}
      >
        {text}
      </span>
    );
  };

  // Currency formatter
  const currencyFormatter = (params) => {
    if (params.value) {
      return new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "CLP",
        minimumFractionDigits: 0,
      }).format(params.value);
    }
    return "$0";
  };

  // Instagram link renderer
  const InstagramCellRenderer = (params) => {
    return (
      <a
        href={`https://instagram.com/${params.value}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-aw-green/85 hover:text-aw-green hover:underline"
      >
        {params.value}
      </a>
    );
  };

  // Column definitions
  const columnDefs = useMemo(
    () => [
      // RUT
      {
        headerName: "RUT",
        field: "rut",
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          filterOptions: ["equals", "greaterThan", "lessThan", "inRange"],
          defaultOption: "greaterThan",
        },
        width: 100,
        headerClass: "text-wrap",
        cellStyle: { color: "hsl(72, 80%, 55%)", textDecoration: "underline" },
      },
      // Nombre
      {
        headerName: "Nombre",
        field: "nombre",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 160,
        headerClass: "text-wrap",
        cellStyle: { color: "hsl(72, 80%, 55%)" },
      },
      // Edad
      {
        headerName: "Edad",
        field: "edad",
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          filterOptions: ["equals", "greaterThan", "lessThan", "inRange"],
          defaultOption: "greaterThan",
        },
        width: 85,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
      // Género
      {
        headerName: "Género",
        field: "genero",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 140,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
      // Instagram
      {
        headerName: "Instagram",
        field: "instagram",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        cellRenderer: InstagramCellRenderer,
        maxWidth: 140,
        headerClass: "text-wrap",
      },
      // Tipo de Ticket
      {
        headerName: "Tipo de Ticket",
        field: "tipoTicket",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 140,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
      // Estado del Cliente
      // {
      //   headerName: "Estado del Cliente",
      //   field: "estadoCliente",
      //   sortable: true,
      //   filter: "agTextColumnFilter",
      //   editable: true,
      //   filterParams: {
      //     filterOptions: ["contains", "startsWith", "endsWith"],
      //     defaultOption: "startsWith",
      //   },
      //   cellRenderer: EstadoClienteCellRenderer,
      //   cellEditor: "agSelectCellEditor",
      //   cellEditorParams: {
      //     values: ["normal", "nuevo", "destacado", "advertencia", "bloqueado"],
      //   },
      //   width: 120,
      //   headerClass: "text-wrap",
      //   cellStyle: {
      //     textAlign: "center",
      //     display: "flex",
      //     justifyContent: "center",
      //     alignItems: "center",
      //   },
      // },
      // Embajador
      {
        headerName: "Embajador",
        field: "embajador",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 140,
        cellStyle: { textAlign: "center", color: "hsl(72, 80%, 55%)" },
      },
      // Lider de Staff
      // {
      //   headerName: "Lider de Staff",
      //   field: "liderStaff",
      //   sortable: true,
      //   filter: "agTextColumnFilter",
      //   filterParams: {
      //     filterOptions: ["contains", "startsWith", "endsWith"],
      //     defaultOption: "startsWith",
      //   },
      //   width: 140,
      //   cellStyle: { textAlign: "center", color: "hsl(20, 60%, 60%)" },
      // },
      // Estado de la Invitación
      // {
      //   headerName: "Estado Invitación",
      //   field: "estadoInvitacion",
      //   sortable: true,
      //   filter: "agTextColumnFilter",
      //   filterParams: {
      //     filterOptions: ["contains", "startsWith", "endsWith"],
      //     defaultOption: "startsWith",
      //   },
      //   width: 140,
      //   headerClass: "text-wrap",
      //   cellStyle: { textAlign: "center" },
      //   cellRenderer: (params) => (
      //     <OpcionesInvitacion
      //       estado={params.value}
      //       rowId={params.data.id}
      //       onSelectionChange={handleSelectionChange}
      //       appliedStatus={appliedStatuses[params.data.id]}
      //       selectedOption={selections[params.data.id]}
      //     />
      //   ),
      // },
      // Horario que accedió
      {
        headerName: "Horario que accedió",
        field: "horarioAccedio",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 140,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
      // Tipo Distribución
      // {
      //   headerName: "Tipo de Distribución",
      //   field: "tipoDistribucion",
      //   sortable: true,
      //   filter: "agTextColumnFilter",
      //   filterParams: {
      //     filterOptions: ["contains", "startsWith", "endsWith"],
      //     defaultOption: "startsWith",
      //   },
      //   width: 140,
      //   headerClass: "text-wrap",
      //   cellStyle: { textAlign: "center" },
      // },
      // Tipo Invitación
      // {
      //   headerName: "Tipo de Invitación",
      //   field: "tipoInvitacion",
      //   sortable: true,
      //   filter: "agTextColumnFilter",
      //   filterParams: {
      //     filterOptions: ["contains", "startsWith", "endsWith"],
      //     defaultOption: "startsWith",
      //   },
      //   width: 140,
      //   headerClass: "text-wrap",
      //   cellStyle: { textAlign: "center" },
      // },
      // Asistencias -- Cuantas veces ha asistido esta persona a los eventos que ha creado la empresa
      // {
      //   headerName: "Asistencias",
      //   field: "asistencias",
      //   sortable: true,
      //   filter: "agTextColumnFilter",
      //   filterParams: {
      //     filterOptions: ["contains", "startsWith", "endsWith"],
      //     defaultOption: "startsWith",
      //   },
      //   width: 120,
      //   headerClass: "text-wrap",
      //   cellStyle: { textAlign: "center" },
      // },
    ],
    [appliedStatuses, selections, handleSelectionChange],
  );

  // Grid options
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: false,
    }),
    [],
  );

  return (
    <div className="w-full h-full">
      {/* Header */}
      <div className="pb-4 ">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">
              Historial de Accesos
            </h2>
          </div>
          {/* <div className="flex items-center space-x-3">
            <AplicarSeleccion
              onApplySelections={applySelections}
              hasSelections={Object.keys(selections).length > 0}
              isLoading={isLoading}
            />
          </div> */}
        </div>
      </div>

      {/* Data Grid */}
      <div className="">
        <div className="ag-theme-quartz w-full" style={{ height: "600px" }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
          />
        </div>
      </div>
    </div>
  );
}
